import {
    Box,
    Button,
    Heading,
    Report,
    Settings,
    StyledIcon,
    TextBlock,
    useSubPage,
    Workflows,
} from "@bcc-ui/ui-components";
import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import style from "./integrations-view-content.scss";
import IntegrationConfigurationModal from "components/asset-management-system/integrations-view/IntegrationConfigurationModal";
import { ManageAssetView } from "components/asset-management-system/manage-assets/ManageAssetView";
import { UploadAssetsModal } from "components/asset-management-system/upload/UploadAssetsModal";
import { Card } from "components/cards/card/Card";
import { AddAssetIcon } from "components/icons/AddAssetIcon";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import StatusBadge, { Status } from "components/status-badge/StatusBadge";
import { AUTH_ASSET_CREATE, AUTH_ASSET_INTEGRATION } from "domain/authority";
import { ScheduleResponse } from "domain/workflows";
import { TemplateTableData } from "domain/workflowTemplates";
import { userSessionService } from "services/user/UserSessionService";
import { templateService, TemplateType } from "services/workflows/TemplateService";
import { workflowService } from "services/workflows/WorkflowService";

export const IntegrationsViewContent = () => {
    const { t } = useTranslation();

    const [isUploadAssetsModalOpen, setIsUploadAssetsModalOpen] = useState(false);
    const [integrationTemplates, setIntegrationTemplates] = useState<TemplateTableData[]>([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState<TemplateTableData | null>(null);
    const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
    const hasFeatureLicense = userSessionService.hasFeatureLicense("FEATURE_ASSET_MANAGEMENT");
    const hasIntegrationAuthority = userSessionService.userHasAnyAuthority([AUTH_ASSET_INTEGRATION]);
    const { setSubPage } = useSubPage();
    const abortControllerRef = useRef<AbortController | null>(null);

    const fetchIntegrationTemplates = () => {
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        setTemplatesLoading(true);
        templateService
            .fetchWorkflowTemplates(
                "",
                "",
                "BMP",
                TemplateType.INTEGRATION,
                [],
                [],
                [],
                true,
                "",
                "",
                "",
                false,
                "",
                undefined,
                abortController
            )
            .then(({ workflowTemplatesTableData }) => {
                const uuidsToFetch = workflowTemplatesTableData
                    .filter((template) => template.saved && template.workflowUuid)
                    .map((template) => template.workflowUuid as string);
                const schedulesPromise =
                    uuidsToFetch.length > 0
                        ? workflowService.fetchSchedules(uuidsToFetch, abortController)
                        : Promise.resolve({ schedules: {} as Record<string, ScheduleResponse> });
                return schedulesPromise.then(({ schedules }) => {
                    workflowTemplatesTableData.forEach((template) => {
                        if (template.workflowUuid && schedules[template.workflowUuid]) {
                            const response = schedules[template.workflowUuid];
                            template.schedule = {
                                frequency: response.frequency,
                                recurrence: response.recurrence,
                                timezone: response.timezone,
                                active: response.enabled,
                            };
                            template.parameters = response.parameters;
                        }
                    });
                    setIntegrationTemplates([...workflowTemplatesTableData]);
                });
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setIntegrationTemplates([]);
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setTemplatesLoading(false);
                }
            });

        return () => {
            abortController.abort();
        };
    };

    useEffect(() => {
        if (!hasIntegrationAuthority) {
            return;
        }

        return fetchIntegrationTemplates();
    }, [hasIntegrationAuthority]);

    return (
        <>
            <Box columnLayout gap={"Sm"} paddingBottom={"Lg"}>
                <TextBlock className={style.subtitle}>{t("Integrations.subTitle")}</TextBlock>
                {hasIntegrationAuthority && (
                    <>
                        {(templatesLoading || integrationTemplates.length > 0) && (
                            <Heading variant="h2">{t("Integrations.tools")}</Heading>
                        )}
                        {templatesLoading ? (
                            <LoadingIndicator />
                        ) : (
                            <div className={classNames(style.cardlayout, style.toolsCards)}>
                                {integrationTemplates.map((template) => (
                                    <Card
                                        icon={template.logoUrl}
                                        key={template.uuid}
                                        title={template.name}
                                        statusBadge={
                                            template.saved ? (
                                                <StatusBadge
                                                    values={[
                                                        {
                                                            status: template.schedule?.active
                                                                ? Status.SUCCESS
                                                                : Status.NEUTRAL,
                                                            title: t(
                                                                template.schedule?.active
                                                                    ? "Common.active"
                                                                    : "Common.inactive"
                                                            ),
                                                        },
                                                    ]}
                                                />
                                            ) : undefined
                                        }
                                        subtitle={
                                            <>
                                                <StyledIcon
                                                    icon={<Workflows size={16} color={"--app-wireIconColor"} />}
                                                    variant={"Square"}
                                                    size={20}
                                                    backgroundColor="--app-wireIconBackgroundColor"
                                                />
                                                {template.editorVersion}
                                            </>
                                        }
                                        content={template.description}
                                        buttons={
                                            <>
                                                {!template.saved && (
                                                    <Button
                                                        small
                                                        variant={"Primary"}
                                                        onClick={() => {
                                                            setSelectedTemplate(template);
                                                            setIsConfigModalOpen(true);
                                                        }}
                                                    >
                                                        {t("Integrations.buttons.integrate")}
                                                    </Button>
                                                )}
                                                {template.saved && (
                                                    <Button
                                                        small
                                                        icon={<Settings />}
                                                        variant={"Tertiary"}
                                                        onClick={() => {
                                                            setSelectedTemplate(template);
                                                            setIsConfigModalOpen(true);
                                                        }}
                                                    >
                                                        {t("Integrations.buttons.viewSettings")}
                                                    </Button>
                                                )}
                                                {template.learnMoreLink && (
                                                    <Button
                                                        small
                                                        variant={"Tertiary"}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            window.open(
                                                                template.learnMoreLink,
                                                                "_blank",
                                                                "noopener,noreferrer"
                                                            );
                                                        }}
                                                    >
                                                        {t("Integrations.buttons.learnMore")}
                                                    </Button>
                                                )}
                                            </>
                                        }
                                    />
                                ))}
                            </div>
                        )}
                    </>
                )}
                <Heading variant={"h2"}>{t("Integrations.uploadData")}</Heading>
                <div className={classNames(style.cardlayout, style.uploadDataCards)}>
                    <Card
                        icon={<StyledIcon icon={<Report />} backgroundColor="--theme-icon-background-default" />}
                        title={t("Integrations.cards.uploadData.uploadCSV.title")}
                        content={t("Integrations.cards.uploadData.uploadCSV.content")}
                        onClick={() => setIsUploadAssetsModalOpen(true)}
                    />

                    {hasFeatureLicense && userSessionService.userHasAllAuthorities([AUTH_ASSET_CREATE]) && (
                        <Card
                            icon={
                                <StyledIcon
                                    backgroundColor={"--theme-icon-background-default"}
                                    icon={<AddAssetIcon color={"var(--theme-icon-fill-default)"} />}
                                />
                            }
                            title={t("Integrations.cards.uploadData.addAsset.title")}
                            content={t("Integrations.cards.uploadData.addAsset.content")}
                            onClick={() => setSubPage(<ManageAssetView />)}
                        />
                    )}
                </div>
            </Box>
            <UploadAssetsModal isOpen={isUploadAssetsModalOpen} onClose={() => setIsUploadAssetsModalOpen(false)} />
            <IntegrationConfigurationModal
                isOpen={isConfigModalOpen}
                onClose={() => setIsConfigModalOpen(false)}
                isSaved={selectedTemplate?.saved ?? false}
                onIntegrationStarted={() => setIsConfigModalOpen(false)}
                onSuccess={fetchIntegrationTemplates}
                templateName={selectedTemplate?.name ?? ""}
                templateUuid={selectedTemplate?.uuid ?? ""}
                parameters={selectedTemplate?.parameters}
                workflowUuid={selectedTemplate?.workflowUuid}
                schedule={selectedTemplate?.schedule}
            />
        </>
    );
};
